import React from "react";
import { OctagonAlert, Shield, Wifi, WifiOff, Power, AlertTriangle, PauseCircle, Download } from "lucide-react";
import { toast } from "sonner";
import { setSgBotKillSwitch, sgExportTradesCsvUrl, sgExportPnlDailyCsvUrl } from "@/lib/smartgrid_api";

const fmtAgo = (ms) => {
    if (!ms) return "—";
    const dt = Date.now() - ms;
    if (dt < 0) return "now";
    const s = Math.floor(dt / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ago`;
};

const Pill = ({ children, color = "#A7B4C6", bg = "rgba(167,180,198,0.10)", border = "rgba(167,180,198,0.20)", testId }) => (
    <span
        data-testid={testId}
        className="inline-flex items-center gap-1.5 h-5 px-2 rounded-full text-[10px] uppercase tracking-[0.14em] font-medium font-mono"
        style={{ background: bg, color, border: `1px solid ${border}` }}
    >
        {children}
    </span>
);

export const SgOperationsPanel = ({ telemetry, onRefresh }) => {
    const tele = telemetry || {};
    const wsTel = tele.ws || {};
    const counters = tele.counters || {};
    const isConnected = !!wsTel.is_connected;
    const killOn = !!tele.kill_switch;

    const onKillToggle = async () => {
        const next = !killOn;
        try {
            await setSgBotKillSwitch(next);
            toast[next ? "warning" : "success"](
                next ? "SmartGrid kill-switch ENGAGED" : "SmartGrid kill-switch released",
                { description: next ? "No new SmartGrid orders. Existing SELLs remain." : "SmartGrid bot resumes placing orders." },
            );
            onRefresh && onRefresh();
        } catch (e) {
            toast.error(`Kill-switch failed: ${e.message}`);
        }
    };

    return (
        <div className="panel" data-testid="sg-operations-panel">
            <div className="px-4 py-3 border-b border-[#1B2A3A] flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Shield size={14} color="#2EE6D6" />
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#A7B4C6]">
                        Operations & Security
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Pill testId="sg-ops-bot-prefix" color="#2EE6D6"
                          bg="rgba(46,230,214,0.10)" border="rgba(46,230,214,0.25)">
                        Owned: {tele.bot_prefix || "sgb-"}*
                    </Pill>
                    {tele.pause_buys && (
                        <Pill testId="sg-ops-pause-buys" color="#FFB020"
                              bg="rgba(255,176,32,0.10)" border="rgba(255,176,32,0.25)">
                            <PauseCircle size={10} /> Buys paused
                        </Pill>
                    )}
                    {isConnected ? (
                        <Pill testId="sg-ops-ws-state" color="#2BE38B"
                              bg="rgba(43,227,139,0.10)" border="rgba(43,227,139,0.25)">
                            <Wifi size={10} /> WS Online
                        </Pill>
                    ) : (
                        <Pill testId="sg-ops-ws-state" color="#FFB020"
                              bg="rgba(255,176,32,0.10)" border="rgba(255,176,32,0.25)">
                            <WifiOff size={10} /> WS Offline
                        </Pill>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-12 gap-0">
                <div className="col-span-12 lg:col-span-5 p-4 border-b lg:border-b-0 lg:border-r border-[#1B2A3A]">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6] mb-1">Kill-Switch</div>
                            <div className="text-xs text-[#6F7D92] max-w-sm">
                                Pauses placement of NEW SmartGrid orders. Existing SELLs remain
                                active to close out open BUY fills. The AI Trade Manager keeps
                                managing open trades (TP trailing) while engaged.
                            </div>
                        </div>
                        <button
                            onClick={onKillToggle}
                            className="btn-anim flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-medium"
                            style={killOn ? {
                                background: "rgba(255,176,32,0.18)", color: "#FFB020",
                                border: "1px solid rgba(255,176,32,0.35)",
                            } : {
                                background: "transparent", color: "#FF5C6C",
                                border: "1px solid rgba(255,92,108,0.35)",
                            }}
                            data-testid="sg-ops-kill-switch-button"
                        >
                            {killOn ? <><OctagonAlert size={14} /> Engaged</> : <><Power size={14} /> Engage</>}
                        </button>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-4 p-4 border-b lg:border-b-0 lg:border-r border-[#1B2A3A]">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6] mb-2">Lifetime Counters</div>
                    <div className="grid grid-cols-2 gap-y-1.5 text-xs font-mono">
                        <div className="text-[#6F7D92]">BUYs placed</div>
                        <div className="text-right text-[#E6EDF6]" data-testid="sg-ops-counter-buy-placed">{counters.buy_placed ?? 0}</div>
                        <div className="text-[#6F7D92]">SELLs placed</div>
                        <div className="text-right text-[#E6EDF6]" data-testid="sg-ops-counter-sell-placed">{counters.sell_placed ?? 0}</div>
                        <div className="text-[#6F7D92]">BUY cancels</div>
                        <div className="text-right text-[#A7B4C6]">{counters.buy_cancel ?? 0}</div>
                        <div className="text-[#6F7D92]">BUY fills</div>
                        <div className="text-right text-[#2BE38B]">{counters.buy_fills ?? 0}</div>
                        <div className="text-[#6F7D92]">SELL fills</div>
                        <div className="text-right text-[#2BE38B]">{counters.sell_fills ?? 0}</div>
                        <div className="text-[#6F7D92]">Foreign ignored</div>
                        <div className="text-right text-[#FFB020]">{counters.foreign_orders_ignored ?? 0}</div>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3 p-4">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6] mb-2">WebSocket Telemetry</div>
                    <div className="grid grid-cols-2 gap-y-1.5 text-xs font-mono">
                        <div className="text-[#6F7D92]">Connects</div>
                        <div className="text-right text-[#E6EDF6]">{wsTel.connect_count ?? 0}</div>
                        <div className="text-[#6F7D92]">Disconnects</div>
                        <div className="text-right text-[#A7B4C6]">{wsTel.disconnect_count ?? 0}</div>
                        <div className="text-[#6F7D92]">Last connect</div>
                        <div className="text-right text-[#A7B4C6]">{fmtAgo(wsTel.last_connect_ms)}</div>
                        <div className="text-[#6F7D92]">Msgs</div>
                        <div className="text-right text-[#A7B4C6]">{wsTel.messages_received ?? 0}</div>
                    </div>
                    {wsTel.last_disconnect_reason && (
                        <div className="mt-2 flex items-start gap-1.5 text-[10px] text-[#FFB020]">
                            <AlertTriangle size={10} className="shrink-0 mt-0.5" />
                            <span className="truncate" title={wsTel.last_disconnect_reason}>
                                {wsTel.last_disconnect_reason}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="px-4 py-2.5 border-t border-[#1B2A3A] flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.14em] text-[#6F7D92] mr-1">Export</span>
                <a href={sgExportTradesCsvUrl()} className="btn-anim inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px]"
                   style={{ color: "#A7B4C6", border: "1px solid #1B2A3A" }} data-testid="sg-export-trades-csv">
                    <Download size={12} /> Trades CSV
                </a>
                <a href={sgExportPnlDailyCsvUrl(90)} className="btn-anim inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px]"
                   style={{ color: "#A7B4C6", border: "1px solid #1B2A3A" }} data-testid="sg-export-pnl-csv">
                    <Download size={12} /> Daily PnL CSV
                </a>
            </div>
        </div>
    );
};

export default SgOperationsPanel;
